import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import Dropzone from "@/components/Dropzone";
import PipelineProgress from "@/components/PipelineProgress";
import VerdictBadge from "@/components/VerdictBadge";
import ImageViewer from "@/components/ImageViewer";
import RegionsList from "@/components/RegionsList";
import ReportTabs from "@/components/ReportTabs";
import ExportPanel from "@/components/ExportPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Cpu, Database, Eye, Loader2, RotateCcw } from "lucide-react";
import { useLang } from "@/contexts/AppContexts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Analyzer = () => {
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("upload");
  const [percent, setPercent] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setStage("upload");
    setPercent(0);
    setSelectedRegionId(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setError(null);
    setResult(null);
    setSelectedRegionId(null);
    setAnalyzing(true);
    setStage("upload");
    setPercent(5);

    const formData = new FormData();
    formData.append("file", file);

    // Animate the stages while server works
    const stageTimer = setTimeout(() => {
      setStage("metadata");
      setPercent(25);
    }, 600);
    const stageTimer2 = setTimeout(() => {
      setStage("ela");
      setPercent(45);
    }, 1400);
    const stageTimer3 = setTimeout(() => {
      setStage("vision");
      setPercent(70);
    }, 2400);

    try {
      const res = await axios.post(`${API}/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (e) => {
          if (e.total) {
            const ratio = Math.min(0.2, e.loaded / e.total / 5);
            setPercent((p) => Math.max(p, Math.round(ratio * 100)));
          }
        },
      });
      clearTimeout(stageTimer);
      clearTimeout(stageTimer2);
      clearTimeout(stageTimer3);
      setStage("done");
      setPercent(100);
      setResult(res.data);
      toast.success(
        `${t("verdict.label")}: ${t(`verdict.${res.data.verdict}`)} (${res.data.confidence}%)`
      );
    } catch (err) {
      clearTimeout(stageTimer);
      clearTimeout(stageTimer2);
      clearTimeout(stageTimer3);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t("errors.upload_failed");
      setError(typeof msg === "string" ? msg : t("errors.upload_failed"));
      toast.error(typeof msg === "string" ? msg : t("errors.upload_failed"));
    } finally {
      setAnalyzing(false);
    }
  };

  const imageSrc =
    result?.image_b64 && `data:image/${result.image_format?.toLowerCase() || "jpeg"};base64,${result.image_b64}`;
  const elaSrc = result?.ela_b64 && `data:image/png;base64,${result.ela_b64}`;

  return (
    <div className="app-shell">
      <Header />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
        {!result && (
          <HeroSection />
        )}

        {/* Upload + analyze */}
        {!result && (
          <Card className="p-4 sm:p-6 space-y-4" data-testid="upload-section">
            <Dropzone
              file={file}
              onFile={(f) => {
                setFile(f);
                setError(null);
              }}
              onClear={reset}
              disabled={analyzing}
            />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {file && !analyzing && (
                <Button
                  variant="ghost"
                  onClick={reset}
                  data-testid="reset-button"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  {t("upload.reset")}
                </Button>
              )}
              <Button
                disabled={!file || analyzing}
                onClick={handleAnalyze}
                size="lg"
                data-testid="analyze-start-button"
                className="min-w-[160px]"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {t("upload.analyzing")}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    {t("upload.analyze_button")}
                  </>
                )}
              </Button>
            </div>
            {analyzing && (
              <div className="pt-2 border-t">
                <PipelineProgress stage={stage} percent={percent} />
              </div>
            )}
            {error && !analyzing && (
              <p
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3"
                data-testid="analyze-error"
              >
                {error}
              </p>
            )}
          </Card>
        )}

        {/* Result */}
        <AnimatePresence>
          {result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="space-y-4"
            >
              <VerdictBadge
                verdict={result.verdict}
                confidence={result.confidence}
                regionsCount={result.gemini?.regions?.length || 0}
                durationMs={result.duration_ms}
                c2paOverride={result.aggregated?.c2pa_override}
                c2paModel={result.aggregated?.c2pa_model}
              />
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
                {/* Left: Viewer + Tabs */}
                <div className="space-y-4">
                  <ImageViewer
                    imageSrc={imageSrc}
                    elaSrc={elaSrc}
                    imageSize={result.metadata?.size}
                    regions={result.gemini?.regions || []}
                    selectedRegionId={selectedRegionId}
                    onRegionClick={(i) => setSelectedRegionId(i)}
                  />
                  <ReportTabs result={result} />
                </div>
                {/* Right rail */}
                <div className="space-y-4">
                  <RegionsList
                    regions={result.gemini?.regions || []}
                    selectedId={selectedRegionId}
                    onSelect={(i) => setSelectedRegionId(i)}
                  />
                  <ExportPanel result={result} />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={reset}
                    data-testid="analyze-new-button"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    {t("nav.analyzeNew")}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const HeroSection = () => {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary font-mono-plex tracking-wider uppercase">
          <Sparkles className="h-3 w-3" />
          metadata · ELA · Gemini 2.5 Pro
        </span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          {t("appName")}
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl">
          {t("tagline")}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FeatureCard
          icon={Database}
          title="Metadata"
          desc="EXIF, software, AI signatures, C2PA"
        />
        <FeatureCard
          icon={Cpu}
          title="Pixel forensics"
          desc="ELA heatmap, noise, frequency analysis"
        />
        <FeatureCard
          icon={Eye}
          title="AI Vision"
          desc="Gemini 2.5 Pro + bounding boxes"
        />
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <Card className="p-4 flex items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <p className="font-display text-sm font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{desc}</p>
    </div>
  </Card>
);

export default Analyzer;
